// CreateMenu — the one Create menu (pen "Studio · Create menu", spec §5;
// its steps pen Q73), opened by "/" at the caret and by "+ Create": its rows
// in groups, top to bottom,
//  - top: Ask here, Create from a source… (a name and a line under it; a
//    row that opens a next step shows a chevron);
//  - Headings: one row of chips (H1 to H6), each named "Heading n";
//  - Insert: the kinds, one row each (only the Note has a line under it);
//  - In this project: the sources (step 1 of Create from a source…);
//  - Make: what to make from the picked source (step 2);
//  - after: one row after a line, no label (Add a source…, Ask here);
// then `foot` when the host gives one ("Lands after <part> · Change").
// A step has a head on top: a back chevron and the step's name (the
// picked source's, wrapped when long). A group with no rows is not shown,
// nor the line above it. "Nothing matches" when no row is left but the
// after group's: at the caret, or once something is typed in the field.
//
// Pure and presentational: the rows come in already narrowed and in this
// order (the host filters as the author types), and a pick goes up as the
// row's place in `rows`. A listbox with aria-activedescendant. How the keys
// reach it:
//  - at the caret (`keys` absent): the text being typed is the filter,
//    keeps focus and moves the cursor;
//  - `filter`: a filter field stands on top ("Type to filter", "Find a
//    source"), takes focus, and ↑/↓/Enter move and pick;
//  - `list`: no field; the list takes focus, and ↑/↓/Enter move and pick.
// Escape is the host's (its popover steps back, then closes).

export type { CreateMenuGroup, CreateMenuRow };
export { CreateMenu, createMenuGroups };

import * as Limits from "@bilda/core/limits";
import { useLingui } from "@lingui/react/macro";
import { type KeyboardEvent, type ReactNode, useEffect, useRef } from "react";
import { Icon, type IconName } from "../Icon/Icon.js";
import "./CreateMenu.css";

type CreateMenuGroup =
    | "top"
    | "headings"
    | "insert"
    | "sources"
    | "make"
    | "after";

// The groups, in the menu's order.
const createMenuGroups: readonly CreateMenuGroup[] = [
    "top",
    "headings",
    "insert",
    "sources",
    "make",
    "after",
];

type CreateMenuRow = {
    // Stable across re-filters.
    readonly key: string;
    readonly group: CreateMenuGroup;
    // What the row shows ("H2" on a chip) and its accessible name
    // ("Heading 2").
    readonly label: string;
    readonly name: string;
    // The line under the name (absent: none).
    readonly hint?: string;
    readonly icon: IconName;
    // The row opens a next step (a chevron at its end).
    readonly more: boolean;
};

// How ↑/↓/Enter reach the menu when the caret does not carry them.
type CreateMenuKeys =
    | {
          readonly type: "filter";
          readonly value: string;
          readonly placeholder: string;
          readonly onChange: (value: string) => void;
          readonly onMove: (direction: "up" | "down") => void;
          readonly onPickActive: () => void;
      }
    | {
          readonly type: "list";
          readonly onMove: (direction: "up" | "down") => void;
          readonly onPickActive: () => void;
      };

const CreateMenu = ({
    label,
    rows,
    activeIndex,
    listboxId,
    onPick,
    onHover,
    keys,
    head,
    disabled,
    foot,
}: {
    // The list's accessible name.
    readonly label: string;
    readonly rows: readonly CreateMenuRow[];
    // The keyboard cursor (a place in `rows`; out of range: none).
    readonly activeIndex: number;
    readonly listboxId: string;
    readonly onPick: (index: number) => void;
    readonly onHover: (index: number) => void;
    readonly keys?: CreateMenuKeys;
    // A step's head: back one step, and the step's name.
    readonly head?: { readonly title: string; readonly onBack: () => void };
    // Offline: the rows show but pick nothing.
    readonly disabled: boolean;
    readonly foot?: ReactNode;
}): ReactNode => {
    const { t } = useLingui();
    const optionId = (index: number) => `${listboxId}-opt-${index}`;
    const placed = rows.map((row, index) => ({ row, index }));
    const groups = createMenuGroups
        .map((group) => ({
            group,
            items: placed.filter((p) => p.row.group === group),
        }))
        .filter((g) => g.items.length > 0);
    const typed =
        keys === undefined || (keys.type === "filter" && keys.value !== "");
    const nothing = typed && rows.every((r) => r.group === "after");

    const groupLabel = (group: CreateMenuGroup): string | undefined => {
        switch (group) {
            case "top":
            case "after":
                return undefined;
            case "headings":
                return t`Headings`;
            case "insert":
                return t`Insert`;
            case "sources":
                return t`In this project`;
            case "make":
                return t({ context: "create from a source", message: "Make" });
            default: {
                const exhaustive: never = group;
                return exhaustive;
            }
        }
    };

    const option = ({
        row,
        index,
    }: {
        readonly row: CreateMenuRow;
        readonly index: number;
    }) => (
        <div
            key={row.key}
            id={optionId(index)}
            role="option"
            // Not a tab stop: focus stays on the text at the caret, the
            // field, or the list (aria-activedescendant).
            tabIndex={-1}
            aria-selected={index === activeIndex}
            aria-disabled={disabled || undefined}
            aria-label={row.name === row.label ? undefined : row.name}
            className={`bilda-create-menu__${row.group === "headings" ? "chip" : "row"}`}
            data-icon={row.icon}
            // On mousedown, not click: the text at the caret must not lose
            // focus (and close the menu) before the pick lands.
            onMouseDown={(event) => {
                event.preventDefault();
                if (!disabled) onPick(index);
            }}
            onMouseEnter={() => onHover(index)}
        >
            {row.group === "headings" ? (
                row.label
            ) : (
                <>
                    <span className="bilda-create-menu__icon">
                        <Icon name={row.icon} size="sm" />
                    </span>
                    <span className="bilda-create-menu__body">
                        <span className="bilda-create-menu__label">
                            {row.label}
                        </span>
                        {row.hint === undefined ? null : (
                            <span className="bilda-create-menu__hint">
                                {row.hint}
                            </span>
                        )}
                    </span>
                    {row.more ? (
                        <span className="bilda-create-menu__more">
                            <Icon name="chevron-right" size="xs" />
                        </span>
                    ) : null}
                </>
            )}
        </div>
    );

    const onKeyDown = (event: KeyboardEvent) => {
        if (keys === undefined) return;
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            keys.onMove(event.key === "ArrowDown" ? "down" : "up");
        } else if (event.key === "Enter") {
            event.preventDefault();
            if (!disabled) keys.onPickActive();
        }
    };
    const activeOption =
        rows[activeIndex] === undefined ? undefined : optionId(activeIndex);
    // In a popover's list that scrolls, the cursor row stays in sight (view
    // only; at the caret the page is not scrolled under the author).
    const inPopover = keys !== undefined;
    // A step with no field: its list takes focus once it opens, so ↑/↓/Enter
    // work at once (React's autoFocus holds for form fields only).
    const list = useRef<HTMLDivElement>(null);
    const listKeys = keys?.type === "list";
    useEffect(() => {
        if (listKeys) list.current?.focus();
    }, [listKeys]);
    useEffect(() => {
        if (activeOption === undefined || !inPopover) return;
        document
            .getElementById(activeOption)
            ?.scrollIntoView?.({ block: "nearest" });
    }, [activeOption, inPopover]);

    return (
        <div className="bilda-create-menu">
            {head === undefined ? null : (
                <button
                    type="button"
                    className="bilda-create-menu__head"
                    aria-label={`${t`Back`}: ${head.title}`}
                    onClick={head.onBack}
                >
                    <Icon name="chevron-left" size="xs" />
                    <span className="bilda-create-menu__title">
                        {head.title}
                    </span>
                </button>
            )}
            {keys?.type === "filter" ? (
                <label className="bilda-create-menu__filter">
                    <Icon name="search" size="xs" />
                    <input
                        type="text"
                        role="combobox"
                        aria-expanded="true"
                        aria-controls={listboxId}
                        aria-autocomplete="list"
                        aria-activedescendant={activeOption}
                        aria-label={keys.placeholder}
                        placeholder={keys.placeholder}
                        // A filter, not a text: a paste past this is cut.
                        maxLength={Limits.SEARCH_QUERY_MAX}
                        value={keys.value}
                        onChange={(event) => keys.onChange(event.target.value)}
                        onKeyDown={onKeyDown}
                        // biome-ignore lint/a11y/noAutofocus: the menu the author just opened is used through this field; the first keystroke filters.
                        autoFocus
                    />
                </label>
            ) : null}
            <div
                id={listboxId}
                role="listbox"
                aria-label={label}
                className="bilda-create-menu__list"
                tabIndex={keys?.type === "list" ? 0 : undefined}
                aria-activedescendant={
                    keys?.type === "list" ? activeOption : undefined
                }
                onKeyDown={keys?.type === "list" ? onKeyDown : undefined}
                ref={list}
            >
                {nothing ? (
                    <p className="bilda-create-menu__empty" role="presentation">
                        {t`Nothing matches`}
                    </p>
                ) : null}
                {groups.map(({ group, items }) => (
                    // biome-ignore lint/a11y/useSemanticElements: a listbox's options are grouped by role="group" (WAI-ARIA listbox pattern); a <fieldset> is a form idiom and not an allowed child of role="listbox".
                    <div
                        key={group}
                        role="group"
                        aria-label={groupLabel(group)}
                        className={`bilda-create-menu__group bilda-create-menu__group--${group}`}
                    >
                        {groupLabel(group) === undefined ? null : (
                            <p
                                className="bilda-create-menu__heading"
                                aria-hidden="true"
                            >
                                {groupLabel(group)}
                            </p>
                        )}
                        {group === "headings" ? (
                            <div className="bilda-create-menu__chips">
                                {items.map(option)}
                            </div>
                        ) : (
                            items.map(option)
                        )}
                    </div>
                ))}
            </div>
            {foot === undefined ? null : (
                <div className="bilda-create-menu__foot">{foot}</div>
            )}
        </div>
    );
};
